import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Briefcase, ShieldCheck, Timer, Users } from "lucide-react";
import { API, FLEET, IMAGES } from "@/data/site";
import PageHero from "@/components/PageHero";
import { Accent, Reveal, SectionHead } from "@/components/Reveal";

const PERKS = [
  { icon: ShieldCheck, title: "Vetted chauffeurs", text: "Background-verified, English-speaking drivers on every trip." },
  { icon: Timer, title: "On-time, every time", text: "Flight tracking and buffer planning baked into every pickup." },
  { icon: Briefcase, title: "Outstation & airport", text: "One-way drops, round trips, multi-day tours and transfers." },
];

const EMPTY = { name: "", email: "", phone: "", vehicle: "Executive Sedan", pickup: "", drop: "", date: "", passengers: 1, notes: "" };

const CabTaxi = () => {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${API}/bookings`, { ...form, passengers: Number(form.passengers) || 1 });
      toast.success("Booking request sent — our dispatch team will confirm shortly.");
      setForm(EMPTY);
    } catch {
      toast.error("Could not send your booking. Please call us instead.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <main data-testid="cab-taxi-page">
      <PageHero
        image={IMAGES.driver}
        eyebrow="Cab & Taxi Services"
        title={<>Your chauffeur is <Accent>already waiting</Accent></>}
        text="Sedans, SUVs and luxury coaches with professional drivers — airport runs to 2,000 km road trips."
      />

      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-20">
        <SectionHead
          eyebrow="The Fleet"
          title={<>Pick your <Accent>ride</Accent></>}
          text="Every vehicle is deep-cleaned, GPS-tracked and driven by someone who treats your trip like their own."
        />
        <div className="grid md:grid-cols-3 gap-6 mt-12">
          {FLEET.map((v, i) => (
            <Reveal key={v.name} delay={i * 0.1}>
              <article className="card-lift group rounded-3xl overflow-hidden border border-white/10 bg-[#121820]">
                <div className="h-56 overflow-hidden">
                  <img
                    src={v.image}
                    alt={v.name}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-110"
                  />
                </div>
                <div className="p-7">
                  <h3 className="font-sora text-lg font-bold">{v.name}</h3>
                  <p className="text-xs text-[#3FB6D3] tracking-wide mt-1">{v.model}</p>
                  <div className="flex items-center gap-5 text-sm text-[#F5F5F0]/60 mt-4">
                    <span className="inline-flex items-center gap-1.5"><Users size={14} /> {v.capacity}</span>
                    <span className="inline-flex items-center gap-1.5"><Briefcase size={14} /> {v.luggage}</span>
                  </div>
                  <p className="mt-5">
                    <span className="font-sora text-2xl font-extrabold text-[#F2C230]">{v.price}</span>
                    <span className="text-xs text-[#F5F5F0]/50"> · all-inclusive of driver</span>
                  </p>
                </div>
              </article>
            </Reveal>
          ))}
        </div>

        <div className="grid sm:grid-cols-3 gap-6 mt-14">
          {PERKS.map((p, i) => (
            <Reveal key={p.title} delay={i * 0.08}>
              <div className="flex gap-4">
                <span className="w-11 h-11 shrink-0 rounded-2xl glass flex items-center justify-center text-[#F2C230]">
                  <p.icon size={19} />
                </span>
                <div>
                  <h3 className="font-sora text-base font-semibold">{p.title}</h3>
                  <p className="text-sm text-[#F5F5F0]/60 mt-1 leading-relaxed">{p.text}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="bg-[#0D131B] border-t border-white/10 py-20">
        <div className="max-w-3xl mx-auto px-5 sm:px-8">
          <SectionHead
            align="center"
            eyebrow="Book a Ride"
            title={<>Tell us where, we'll handle <Accent>how</Accent></>}
          />
          <Reveal delay={0.15}>
            <form
              onSubmit={submit}
              data-testid="cab-booking-form"
              className="glass rounded-3xl p-7 sm:p-10 mt-10 grid sm:grid-cols-2 gap-5"
            >
              <input required placeholder="Full name" value={form.name} onChange={set("name")} data-testid="cab-input-name" className="input-dark" />
              <input required type="tel" placeholder="Phone number" value={form.phone} onChange={set("phone")} data-testid="cab-input-phone" className="input-dark" />
              <input required type="email" placeholder="Email address" value={form.email} onChange={set("email")} data-testid="cab-input-email" className="input-dark sm:col-span-2" />
              <select value={form.vehicle} onChange={set("vehicle")} data-testid="cab-select-vehicle" className="input-dark sm:col-span-2">
                {FLEET.map((v) => (
                  <option key={v.name} value={v.name} className="bg-[#121820]">
                    {v.name} — {v.price}
                  </option>
                ))}
              </select>
              <input required placeholder="Pickup location" value={form.pickup} onChange={set("pickup")} data-testid="cab-input-pickup" className="input-dark" />
              <input required placeholder="Drop location" value={form.drop} onChange={set("drop")} data-testid="cab-input-drop" className="input-dark" />
              <input required type="date" value={form.date} onChange={set("date")} data-testid="cab-input-date" className="input-dark [color-scheme:dark]" />
              <input type="number" min="1" max="24" placeholder="Passengers" value={form.passengers} onChange={set("passengers")} data-testid="cab-input-passengers" className="input-dark" />
              <textarea placeholder="Anything we should know? (child seat, extra stops…)" value={form.notes} onChange={set("notes")} rows={3} data-testid="cab-input-notes" className="input-dark sm:col-span-2 resize-none" />
              <button type="submit" disabled={busy} data-testid="cab-submit-button" className="btn-gold sm:col-span-2 disabled:opacity-60">
                {busy ? "Sending…" : "Request Booking"}
              </button>
            </form>
          </Reveal>
        </div>
      </section>
    </main>
  );
};

export default CabTaxi;
